import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarDays, Phone, Mail, Building2, MessageCircle, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { leadService } from '../../services/api';
import { KANBAN_STATUSES, STATUS_COLORS } from '../../components/leads/leadUtils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function AdminDemosPage() {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { tag: 'demo', limit: 200 };
      if (filter !== 'all') params.status = filter;
      const data = await leadService.getAll(params);
      setDemos(data);
    } catch {
      toast.error('Error al cargar solicitudes de demo');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (lead, newStatus) => {
    try {
      const updated = await leadService.update(lead.id, { status: newStatus });
      setDemos((prev) => prev.map((d) => (d.id === lead.id ? updated : d)));
      const label = KANBAN_STATUSES.find((s) => s.value === newStatus)?.label || newStatus;
      toast.success(`Estado actualizado a "${label}"`);
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "d MMM yyyy 'a las' HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const waLink = (phone, nombre) =>
    `https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
      `Hola ${nombre}, te contactamos desde AutoFlow por tu solicitud de demo.`
    )}`;

  const stats = {
    total: demos.length,
    new: demos.filter((d) => d.status === 'new').length,
    active: demos.filter((d) => ['in_conversation', 'interested', 'contacted'].includes(d.status)).length,
    closed: demos.filter((d) => ['closed_won', 'closed_lost', 'closed'].includes(d.status)).length,
  };

  return (
    <div className="space-y-6" data-testid="admin-demos-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Solicitudes de Demo</h1>
        <p className="text-sm text-slate-500 mt-1">
          Gestión de solicitudes recibidas desde la landing page
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: stats.total, color: 'text-slate-900' },
          { label: 'Nuevas', value: stats.new, color: 'text-blue-600' },
          { label: 'En curso', value: stats.active, color: 'text-amber-600' },
          { label: 'Cerradas', value: stats.closed, color: 'text-green-600' },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="pt-5 pb-4 text-center">
              <div className={`text-3xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-slate-500 mt-1">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-slate-400" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {KANBAN_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Cargando...</div>
      ) : demos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400">
            No hay solicitudes de demo{filter !== 'all' ? ' con ese estado' : ''}.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {demos.map((d) => {
            const customer = d.customer || {};
            const nombre = customer.fullName || customer.firstName || 'Sin nombre';
            const phone = customer.phone || '';
            const email = customer.email || '';
            const statusLabel =
              KANBAN_STATUSES.find((s) => s.value === d.status)?.label || d.status;

            return (
              <Card key={d.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900 text-lg">{nombre}</h3>
                        <Badge className={STATUS_COLORS[d.status] || 'bg-slate-100 text-slate-600'}>
                          {statusLabel}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
                        {d.message && d.message.startsWith('Agencia:') && (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                            <span>{d.message.replace('Agencia: ', '')}</span>
                          </div>
                        )}
                        {phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                            <span>{phone}</span>
                          </div>
                        )}
                        {email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="truncate">{email}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <CalendarDays className="w-4 h-4 text-slate-400 shrink-0" />
                          <span>{formatDate(d.createdAt)}</span>
                        </div>
                      </div>

                      {d.message && !d.message.startsWith('Agencia:') && (
                        <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm text-slate-600 border border-slate-100">
                          <MessageCircle className="w-3.5 h-3.5 inline mr-1.5 text-slate-400" />
                          {d.message}
                        </div>
                      )}
                    </div>

                    <div className="flex md:flex-col items-center gap-2 shrink-0">
                      <Select value={d.status} onValueChange={(val) => updateStatus(d, val)}>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {KANBAN_STATUSES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {phone && (
                        <a
                          href={waLink(phone, nombre)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-green-600 border-green-200 hover:bg-green-50 w-full"
                          >
                            <Phone className="w-3.5 h-3.5 mr-1.5" />
                            WhatsApp
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
